import React, { useEffect, useState } from "react";
import PanelShell from "../../shared/PanelShell";
import { SkeletonChart } from "../../shared/Skeleton";
import { fmtNumber, titleCase } from "../../shared/format";
import { getKnowledgeGaps } from "../../../api/conversation";
import { sortKnowledgeGaps } from "../lib/sortKnowledgeGaps";
import CrossChannelNote from "./crossChannelNote";
import Pagination from "../../shared/Pagination";
import { ROW_HOVER } from "./rowStyles";

// fn_get_knowledge_gaps items. incorrect_information sorts to the top regardless
// of count (sortKnowledgeGaps). Fetches its own data — the function takes no date
// pair, so it cannot join the date-scoped page-load batch.
//
// IT IS MODALITY-SCOPED, and this comment used to deny it. The parameters are
// p_client_id, p_status, p_min_occurrences, p_limit and p_modality; this panel
// passes a modality and has since 2026-08-27, which is what keeps voice gaps off
// the chat page. `p_status:'open'` became a default on 2026-08-28 — measured at
// Verde as dropping zero rows, so this panel's 50-row population is unchanged;
// see the note on getKnowledgeGaps in api/conversation.js.
//
// TWO KINDS OF ROW, AND THEY MUST NOT READ ALIKE.
//
//   knowledge_gap / unanswered_question — the site did not answer something.
//     The finding IS the topic: "Check-in time, asked 7 times."
//
//   incorrect_information — the agent answered, and answered WRONGLY. The
//     finding is not the topic at all; it is the gap between `agent_said` and
//     `correct_answer`. Rendering only the topic turns "the agent told seven
//     guests check-in is 2 PM when it is 3 PM" into "your website is missing
//     check-in information", which is a different problem with a different fix —
//     and on Verde Ranch that misreading covers 34 of 50 rows, the whole head of
//     a panel that pins this class to the top.
//
// `agent_said` and `correct_answer` are guest-facing content strings, not vendor
// or database internals, so Part F does not keep them off the page.
//
// `example_questions` is empty on 46 of 50 live rows, so it cannot carry a row
// on its own; `example_conversations` is populated on 50 of 50 and is an exact
// join key, which is what the "links to the conversations" requirement wanted.
// A row raises a selection through the same panel-filter plumbing the topic bars
// use, but by conversation id rather than by matching words.
//
// THAT MAKES THE KEY EXACT AND THE POPULATIONS STILL DISJOINT — an earlier
// version of this comment claimed the selection "can never miss", and it can.
// fn_get_knowledge_gaps takes no dates, so these gaps are ALL-TIME; the list is
// scoped to the picker's range. Measured on the frozen probe: 50 gaps carry 65
// distinct conversation ids, of which 6 fall in the default window's first
// page. So a row can offer "Show 3 conversations" and narrow the list to
// nothing — not because the join failed, but because those conversations are
// outside the dates on screen.
//
// The id join means it cannot match the WRONG conversation, which is the failure
// the topic bars have. It does not mean it always matches one. The list says
// which of the two happened rather than showing the generic empty state, and
// BE-2 (`p_modality`) and a date-scoped gaps RPC are what would close it
// properly.
// §2.4: "website gaps show five." It was 10, which is what every other paged
// panel in this app uses — a house convention rather than a decision about this
// panel. The spec is narrower on purpose: since C1 each row is a question
// rendered as the hero at readable size, so five rows is a screenful and ten is
// a wall. The pager still says what it is showing, so nothing is hidden.
const GAPS_PER_PAGE = 5;

// What the fetch asks for. Kept here rather than inline at the call site because
// the last page has to say something true about it: a response of exactly this
// length is indistinguishable from "there were more and you got the first 50".
const FETCH_LIMIT = 50;

const INCORRECT = "incorrect_information";

const GAP_TONE = {
  [INCORRECT]: "bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 border-amber-300 dark:border-amber-800",
};
const NEUTRAL_TONE =
  "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700";

// The class label carries the distinction in words as well as in colour, so it
// survives greyscale and print — owners forward these panels.
const GAP_LABEL = {
  [INCORRECT]: "Agent said something wrong",
  knowledge_gap: "Not answered",
  unanswered_question: "Not answered",
  local_knowledge: "Asked about the area",
};

/**
 * The guest's own words for a gap, or null.
 *
 * `example_questions` is empty on 46 of 50 live rows, so this returns null far
 * more often than not — which the row treatment above is built around rather
 * than surprised by. Whitespace-only entries count as absent: a quote containing
 * a space is not a question anybody asked.
 */
export const questionOf = (item) => questionsListOf(item)[0] ?? null;

// The non-empty example questions of a row OR of a single correction, as clean
// strings. A correction carries only the questions that triggered ITS wrong answer,
// which is what lets each one sit beside the right correction (markup #14).
const questionsListOf = (obj) => {
  const list = Array.isArray(obj?.example_questions) ? obj.example_questions : [];
  return list
    .map((q) => (typeof q === "string" ? q.trim() : String(q?.question || q?.text || "").trim()))
    .filter(Boolean);
};

const WebsiteGapsPanel = ({ panelKey, label = "Website gaps", say, clientId, modality = null, crossChannel = false, onFilter, onSelect }) => {
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setLoading(true);
    // Back to the first page with the client. Left where it was, a reader who
    // was on page 4 of one property lands mid-list on the next one, or past the
    // end of a shorter list entirely.
    setPage(0);
    getKnowledgeGaps(clientId, { limit: FETCH_LIMIT, modality })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { console.error("website_gaps:", e); if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, modality]);
  const sorted = sortKnowledgeGaps(data?.items);

  // TEN AT A TIME, PAGED — not ten and then all fifty on one click.
  // Verde Ranch returns 50 gaps of which 33 were asked exactly once. Revealing
  // them all at once put 2,445px of page under a single button, most of it the
  // tail; pages keep the panel a fixed size whichever part of the list you are
  // reading, and give the reader somewhere to come back to.
  //
  // PAGED AFTER THE SORT, NEVER BEFORE. `sortKnowledgeGaps` pins
  // incorrect_information above everything regardless of how often it was asked
  // — that ordering is the panel's whole point, and it only means anything if
  // page one holds the head of it. Slicing the RPC's own order and sorting
  // within a page would put a wrong answer on page four.
  //
  // Client-side over what one fetch already returned. Per-page RPC calls would
  // buy nothing: the function takes no offset, so every page would refetch the
  // same fifty rows.
  const pageCount = Math.max(1, Math.ceil(sorted.length / GAPS_PER_PAGE));
  // Clamped rather than trusted. The row count changes under this when a fetch
  // resolves, and a page index that outlived a shorter list renders an empty
  // panel with working controls — which reads as no data rather than as a lost
  // position.
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * GAPS_PER_PAGE;
  const shown = sorted.slice(start, start + GAPS_PER_PAGE);
  const onLastPage = safePage >= pageCount - 1;
  // The response came back exactly as long as the request allowed, so the
  // fiftieth row is not evidence of a fiftieth gap. See the caption below.
  const fetchWasCapped = sorted.length === FETCH_LIMIT;

  return (
    <PanelShell panelKey={panelKey} label={label} say={say} hasData={sorted.length > 0} loading={loading} fullWidth>
      {loading ? <SkeletonChart height={180} /> : (
        <ul className="divide-y divide-gray-200 dark:divide-gray-800">
          {shown.map((it, i) => {
            const isWrong = it.gap_type === INCORRECT;
            const conversations = Array.isArray(it.example_conversations) ? it.example_conversations : [];
            // Addendum 1 §5c: speech recognition is confidently wrong on the proper
            // nouns and numbers these claims are made of. A `from_transcript` (or
            // `mixed`) claim must never be presented as something the agent
            // definitely said — it carries the caveat instead. `exact` (or an
            // absent value, the pre-addendum rows) renders normally.
            const fromTranscript = it.verification === "from_transcript" || it.verification === "mixed";
            return (
              <li key={it.id ?? start + i} className={`py-3 ${ROW_HOVER}`}>
                {/* THE QUESTION IS THE HERO — spec C1, "In guests' own words":
                    "The question text is the hero of each row, at readable size,
                    in quotes. Beneath it, quieter: the topic, occurrences, and
                    incorrect_occurrences when non-zero."
                    This inverts what was here: the topic led and the question
                    was secondary, which reads as a content audit rather than as
                    people asking things.

                    THE DATA DOES NOT ALWAYS ALLOW IT, and the fallback is
                    deliberate rather than incidental: `example_questions` is
                    empty on 46 of 50 live rows. A row with no question keeps the
                    TOPIC as its hero — an empty pair of quotes would be worse
                    than the treatment this replaced. So the panel reads as
                    guests' own words wherever the words exist, and as a topic
                    list where they do not, which is honest about which is which
                    rather than uniform and half-invented. */}
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <span className="min-w-0 break-words">
                    {questionOf(it) ? (
                      <span className="gi-takeaway text-gray-900 dark:text-white" data-testid="gap-question">
                        {`\u201C${questionOf(it)}\u201D`}
                      </span>
                    ) : (
                      <span className="gi-body font-medium text-gray-900 dark:text-gray-100" data-testid="gap-topic-hero">
                        {titleCase(it.topic || it.gap_type || "Gap")}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border ${isWrong ? GAP_TONE[INCORRECT] : NEUTRAL_TONE}`}>
                      {GAP_LABEL[it.gap_type] || "Gap"}
                    </span>
                    {/* A quiet chip that says the claim came off a call transcript,
                        so the distinction survives even where the row is skimmed. */}
                    {fromTranscript && (
                      <span
                        data-testid="gap-verification-chip"
                        className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border ${NEUTRAL_TONE}`}
                      >
                        From transcript
                      </span>
                    )}
                    {/* HOW MANY of the topic's occurrences were the agent stating
                        something false. Rows are clustered by canonical topic as
                        of 2026-08-27, and `gap_type` is the type accounting for
                        the MOST occurrences — no longer "any wrong row wins",
                        which used to flag a whole group as incorrect_information
                        on one bad answer in 64 and made the priority signal
                        worthless.
                        The consequence is that a wrong answer can now hide inside
                        a topic typed as something else: live, "Site availability"
                        is an unanswered_question with two wrong answers in it,
                        and nothing else on the row says so. This badge is the
                        only place that shows. It does NOT relabel the topic —
                        the class chip keeps saying what the topic mostly is. */}
                    {Number(it.incorrect_occurrences) > 0 && (
                      <span
                        className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border ${GAP_TONE[INCORRECT]}`}
                        title={`${fmtNumber(it.incorrect_occurrences)} of ${fmtNumber(it.occurrences)} were the agent saying something wrong`}
                      >
                        {fmtNumber(it.incorrect_occurrences)} wrong
                      </span>
                    )}
                    {it.occurrences != null && (
                      <span className="gi-label text-gray-500 dark:text-gray-400">{fmtNumber(it.occurrences)}×</span>
                    )}
                  </span>
                </div>

                {/* The topic, quieter, beneath the question it belongs to. Only
                    when the question is the hero — otherwise the topic IS the
                    hero above and repeating it here says nothing twice. */}
                {questionOf(it) && it.topic && (
                  <p className="gi-body text-gray-600 dark:text-gray-400 mt-0.5" data-testid="gap-topic">
                    {titleCase(it.topic)}
                  </p>
                )}

                {/* corrections[] — each wrong answer paired with ITS OWN correction
                    and the questions that triggered it, so a check-out question can
                    never sit beside a check-in-time fix (markup #14). The backend now
                    keeps each self-contained; before, a single top-level agent_said was
                    shown against questions aggregated across the whole topic cluster.
                    A row that predates corrections[] falls through to the single-pair
                    block below. */}
                {Array.isArray(it.corrections) && it.corrections.length > 0 && (
                  <div className="mt-1.5 space-y-3" data-testid="gap-corrections">
                    {it.corrections.map((c, ci) => {
                      const cFrom = [c.verification ?? it.verification].some(
                        (v) => v === "from_transcript" || v === "mixed",
                      );
                      const cMeans = c.verification_means ?? it.verification_means;
                      return (
                        <div key={c.id ?? ci} className="space-y-0.5" data-testid="gap-correction">
                          {/* the guest question(s) that led to THIS wrong answer */}
                          {questionsListOf(c).slice(0, 2).map((q, qi) => (
                            <p key={qi} className="gi-body break-words text-gray-800 dark:text-gray-100">{`“${q}”`}</p>
                          ))}
                          {c.agent_said && (
                            <p className="gi-body break-words text-gray-700 dark:text-gray-200">
                              <span className="text-gray-500 dark:text-gray-400">
                                {cFrom ? "Flagged from a call transcript: " : "The agent said "}
                              </span>
                              <span className="font-semibold">{`“${c.agent_said}”`}</span>
                            </p>
                          )}
                          {c.correct_answer && (
                            <p className="gi-body break-words text-gray-700 dark:text-gray-200">
                              <span className="text-gray-500 dark:text-gray-400">Actually </span>
                              {`“${c.correct_answer}”`}
                            </p>
                          )}
                          {c.occurrences != null && (
                            <p className="gi-label text-gray-500 dark:text-gray-400">Said {fmtNumber(c.occurrences)}×</p>
                          )}
                          {cFrom && cMeans && (
                            <p data-testid="gap-verification-means" className="gi-body break-words text-gray-500 dark:text-gray-400">{cMeans}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* What the agent actually said, against what is true. This IS
                    the finding on these rows — without it the topic alone reads
                    as a missing page rather than a wrong answer. LEGACY single-pair
                    path: only when the row carries no self-contained corrections[]. */}
                {!(it.corrections?.length > 0) && isWrong && (it.agent_said || it.correct_answer) && (
                  <div className="mt-1.5 space-y-0.5">
                    {/* Sentence case, NOT `.gi-label`. That token uppercases,
                        which is right for a caption standing over a number and
                        wrong for a lead-in that runs into the quote after it —
                        it rendered "THE AGENT SAID “For RV sites…”".
                        THE WRONG ANSWER CARRIES THE WEIGHT, and only the quote
                        does. Ten of these rows rendered at one weight is the
                        flat wash Part C exists to break, on the panel the spec
                        calls the most valuable on the page — but the emphasis
                        belongs on what the agent actually said, not on the words
                        "The agent said". The lead-in stays muted so the eye
                        lands on the claim rather than the scaffolding.
                        WEIGHT, NOT HUE. Colour was the first instruction and was
                        withdrawn for this, which is the stronger tool: it needs
                        no contrast measurement, survives greyscale and print
                        unchanged, and does not spend the palette's "red means
                        act" on a row that already says so in words.
                        `font-semibold` (600) and not `font-bold`: 600 is what
                        --type-verdict-weight and --type-number-weight use, so
                        this is the repo's emphasis step. 700 belongs to the hero
                        number alone. No `gi-*` token fits — that scale moves
                        size and weight together, and this row must not change
                        size.
                        The "Actually" row below is deliberately NOT emphasised.
                        Two bold lines are one flat wash again, and the contrast
                        between them is what makes the finding readable. */}
                    {/* On a from_transcript row the lead-in is "Flagged from a call
                        transcript", NOT "The agent said" — the claim is a
                        transcription that may have misheard a proper noun or a
                        number, and must not read as a definite quote. */}
                    {it.agent_said && (
                      <p className="gi-body break-words text-gray-700 dark:text-gray-200">
                        <span className="text-gray-500 dark:text-gray-400">
                          {fromTranscript ? "Flagged from a call transcript: " : "The agent said "}
                        </span>
                        <span className="font-semibold">“{it.agent_said}”</span>
                      </p>
                    )}
                    {it.correct_answer && (
                      <p className="gi-body break-words text-gray-700 dark:text-gray-200">
                        <span className="text-gray-500 dark:text-gray-400">Actually </span>
                        “{it.correct_answer}”
                      </p>
                    )}
                    {/* The caveat, verbatim, as the quiet metadata line beneath the
                        claim — never collapsed to a single panel-level note, because
                        the reader needs it beside the claim it qualifies. */}
                    {fromTranscript && it.verification_means && (
                      <p data-testid="gap-verification-means" className="gi-body break-words text-gray-500 dark:text-gray-400">
                        {it.verification_means}
                      </p>
                    )}
                  </div>
                )}

                {!(it.corrections?.length > 0) && it.example_questions?.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {it.example_questions.slice(0, 2).map((q, j) => (
                      <li key={j} className="gi-body break-words text-gray-500 dark:text-gray-400">"{q}"</li>
                    ))}
                  </ul>
                )}

                {/* The conversations this came from. An exact id join, so the
                    selection it raises cannot miss the way a text match can. */}
                {conversations.length > 0 && (onSelect || onFilter) && (
                  <button
                    type="button"
                    onClick={() => (onSelect
                      // §2.3: a drill does three things at once — narrows the
                      // rail, opens the level, marks the source. `onSelect` is
                      // `openGap`, which raises the filter itself, so this is one
                      // call rather than two a caller remembers to order.
                      //
                      // `openGap` HAD NO CALLER UNTIL NOW. It was written in C1
                      // and nothing referenced it — the fourth time in this
                      // feature that a mechanism shipped with its wiring
                      // inferred. Nobody noticed because the one E2E test that
                      // would have caught it was skipping itself on a locator
                      // that ran before the panel rendered.
                      ? onSelect(it, (it.example_questions || [])[0])
                      : onFilter({
                        source: panelKey,
                        kind: "conversations",
                        value: conversations,
                        label: titleCase(it.topic || it.gap_type || "Gap"),
                      }))}
                    className="mt-1 inline-flex items-center min-h-[44px] text-sm font-medium gi-link transition-colors no-underline"
                  >
                    Show {fmtNumber(conversations.length)} {conversations.length === 1 ? "conversation" : "conversations"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <CrossChannelNote when={crossChannel} className="mt-3" />

      {/* THE SHARED CONTROL, not a bespoke one — `Previous [1][2][3] Next` is
          what every other paged panel in this app renders, and a panel that
          paginates differently from the ten beside it is its own small defect.
          It is 1-BASED; the slice above is 0-based, so the conversion happens
          here at the boundary rather than by reworking the slice.
          It renders its own "Showing 1–10 of 50 gaps". That denominator is
          honest ONLY because `totalItems` is the LOADED count and the sentence
          below says the loaded count may not be the whole of it. Passing
          anything else there — a guess at the property's real total — would make
          it a lie. */}
      {!loading && (
        <Pagination
          page={safePage + 1}
          totalPages={pageCount}
          onPageChange={(p) => setPage(p - 1)}
          totalItems={sorted.length}
          perPage={GAPS_PER_PAGE}
          label="gaps"
        />
      )}

      {/* Said on the last page, where the reader would otherwise take the
          control's "of 50" as the property's total. It describes the LIST, not
          this panel's request — a reader has no mental model of a panel making a
          fetch, and the machinery is not their problem.
          NOT "the 50 most frequent": the sort pins incorrect_information above
          everything regardless of count, and the RPC chooses which fifty it
          returns by an order this code cannot see. Neither the panel nor the
          reader can say these are the most frequent, so it does not. */}
      {onLastPage && fetchWasCapped && !loading && (
        <p className="gi-body text-gray-500 dark:text-gray-400 mt-2">
          There may be more than these {fmtNumber(FETCH_LIMIT)}.
        </p>
      )}
    </PanelShell>
  );
};

export default WebsiteGapsPanel;
